import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/Reveal";
import { getArtworks, getExhibitions } from "@/lib/api";

const HERO_TEXTURE = "/assets/phoenix-moon.webp";

const PATHWAYS = [
  {
    index: "01",
    title: "For Art Lovers",
    body: "Originals, prints, and custom commissions. Surrealist works that give you permission to shift and evolve.",
    to: "/gallery",
    cta: "Enter the gallery",
    accent: "text-royal",
    testid: "pathway-art-lovers",
  },
  {
    index: "02",
    title: "For Seekers",
    body: "Art as mirror, not spectacle. Explore the philosophy of healing, identity, and the transcendence of ego.",
    to: "/philosophy",
    cta: "Read the philosophy",
    accent: "text-flame",
    testid: "pathway-seekers",
  },
  {
    index: "03",
    title: "For Arts Professionals",
    body: "Exhibitions, curatorial philosophy, community impact, and a documented professional record.",
    to: "/curatorial",
    cta: "View the practice",
    accent: "text-azure",
    testid: "pathway-professionals",
  },
];

const VOICES = [
  {
    quote:
      "Seyoun is a multi faceted curator, artist, visionary. Her ability to bring people together through their love and support of the arts is truly inspiring.",
    name: "Chelsea Kennedy",
    role: "Artist, HerArtistry Art Exhibition",
  },
  {
    quote:
      "Her dedication, professionalism, and genuine passion for helping artists succeed made all the difference. I would highly recommend working with her to any artist looking to make their mark.",
    name: "Alvaro Duran",
    role: "Artist",
  },
  {
    quote:
      "Last night's exhibit was INCREDIBLE! We were both inspired, empowered, and emotional. Truly touched!",
    name: "Jai Stevenson-Smith",
    role: "Attendee",
  },
];

export default function HomePage() {
  useSEO(
    "The House of Ego · The Transcendence of Ego Through Creative Expression",
    "The House of Ego is the creative identity of surrealistic artist and curator Seyoun Taylor, a sanctuary bridging art, healing, and storytelling. Explore artwork, exhibitions, and collaborations."
  );
  const [featured, setFeatured] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    getArtworks()
      .then((arts) => setFeatured(arts.filter((a) => a.featured).slice(0, 4)))
      .catch(() => {});
    getExhibitions()
      .then((ex) => setUpcoming(ex.filter((e) => e.status === "upcoming")))
      .catch(() => {});
  }, []);

  return (
    <main id="main-content" data-testid="home-page">
      {/* Hero · the dictionary definition */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: `url(${HERO_TEXTURE})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "auto 120%",
            backgroundPosition: "right -4% center",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-40 md:pt-52 pb-24 md:pb-36">
          <Reveal>
            <p className="eyebrow" data-testid="hero-welcome">Welcome to…</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-serif tracking-tight text-ink text-6xl sm:text-7xl lg:text-8xl" data-testid="hero-title">
              The House of Ego
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-sm md:text-base tracking-wide text-neutral-500" data-testid="hero-phonetic">
              / haʊs ʌv ˈiː.ɡoʊ / · noun, proper
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <ol className="mt-10 max-w-2xl space-y-4 font-serif text-2xl md:text-3xl leading-snug text-ink">
              <li className="flex gap-4" data-testid="hero-definition-1">
                <span className="text-royal text-lg mt-1.5 font-sans">1.</span>
                <span>The transcendence of ego through creative expression.</span>
              </li>
              <li className="flex gap-4 text-neutral-600" data-testid="hero-definition-2">
                <span className="text-royal text-lg mt-1.5 font-sans">2.</span>
                <span>
                  A creative sanctuary bridging art, healing, and storytelling through curated community
                  experience.
                </span>
              </li>
            </ol>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/gallery"
                data-testid="hero-cta-gallery"
                className="bg-royal text-white px-7 py-3.5 text-sm tracking-wide hover:bg-royal-deep transition-colors inline-flex items-center gap-2"
              >
                Enter the Gallery <ArrowRight size={16} />
              </Link>
              <Link
                to="/curatorial"
                data-testid="hero-cta-curatorial"
                className="border border-neutral-300 px-7 py-3.5 text-sm tracking-wide text-ink hover:border-royal hover:text-royal transition-colors"
              >
                Curatorial Practice
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.5}>
            <p className="mt-16 text-xs uppercase tracking-[0.25em] text-neutral-400">
              Artist &amp; Curator · Seyoun Taylor · Dallas–Fort Worth, Texas
            </p>
          </Reveal>
        </div>
      </section>

      {/* Three pathways */}
      <section className="rule" data-testid="pathways-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
          <Reveal>
            <p className="eyebrow">Find your way in</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink max-w-xl">
              One house, three doors.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px bg-neutral-200 md:grid-cols-3 border border-neutral-200">
            {PATHWAYS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.12} className="bg-white">
                <Link to={p.to} data-testid={p.testid} className="group block h-full p-8 md:p-10">
                  <p className={`text-xs tracking-[0.25em] ${p.accent}`}>{p.index}</p>
                  <h3 className="mt-6 font-serif text-2xl md:text-3xl text-ink">{p.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-neutral-600">{p.body}</p>
                  <p className="mt-8 inline-flex items-center gap-2 text-sm text-ink group-hover:text-royal transition-colors">
                    {p.cta}
                    <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured artwork */}
      <section className="rule" data-testid="featured-artwork-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
          <Reveal className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <p className="eyebrow">The Artwork</p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
                Selected works
              </h2>
            </div>
            <Link
              to="/gallery"
              data-testid="featured-view-all-link"
              className="link-underline text-sm text-royal inline-flex items-center gap-1.5"
            >
              View all artwork <ArrowRight size={15} />
            </Link>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {featured.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.1}>
                <Link to={`/gallery/${a.slug}`} data-testid={`featured-artwork-${a.slug}`} className="group block">
                  <div className="overflow-hidden bg-stone2 aspect-[4/5]">
                    <img
                      src={a.image_url}
                      alt={`${a.title}, artwork by Seyoun Taylor`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <p className="mt-4 font-serif text-lg text-ink">{a.title}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-1">
                    {[a.series, a.size].filter(Boolean).join(" · ")}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="bg-royal text-white" data-testid="artist-quote-section">
        <div className="mx-auto max-w-5xl px-6 md:px-10 py-20 md:py-28 text-center">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.3em] text-white/60">Seyoun Thee Artist</p>
            <blockquote className="mt-8 font-serif text-3xl md:text-5xl leading-tight tracking-tight">
              “Through my work, I project a unique essence that gives viewers permission to shift and
              evolve alongside me.”
            </blockquote>
            <p className="mt-8 text-sm text-white/70">Seyoun Taylor</p>
          </Reveal>
        </div>
      </section>

      {/* Upcoming exhibitions */}
      {upcoming.length > 0 && (
        <section className="rule" data-testid="upcoming-exhibitions-section">
          <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
            <Reveal>
              <p className="eyebrow">Upcoming</p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
                On the calendar
              </h2>
            </Reveal>
            <div className="mt-12 space-y-0">
              {upcoming.map((ex, i) => (
                <Reveal key={ex.slug} delay={i * 0.1}>
                  <Link
                    to="/curatorial"
                    data-testid={`upcoming-exhibition-${ex.slug}`}
                    className="group grid md:grid-cols-[120px_1fr_auto] gap-4 md:gap-10 items-baseline py-8 border-t border-neutral-200 last:border-b"
                  >
                    <p className="text-xs uppercase tracking-[0.2em] text-flame">{ex.date}</p>
                    <div>
                      <h3 className="font-serif text-2xl md:text-3xl text-ink group-hover:text-royal transition-colors">
                        {ex.title}
                      </h3>
                      <p className="mt-2 text-sm text-neutral-600 max-w-2xl">{ex.summary}</p>
                      <p className="mt-3 text-xs uppercase tracking-[0.2em] text-flame">RSVP open →</p>
                    </div>
                    <p className="text-sm text-neutral-500">{ex.location}</p>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Community voices */}
      <section className="rule" data-testid="community-voices-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
          <Reveal>
            <p className="eyebrow">Community Voices</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink max-w-2xl">
              What the community carries home
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-12 md:grid-cols-3">
            {VOICES.map((v, i) => (
              <Reveal key={v.name} delay={i * 0.12}>
                <figure className="border-l-2 border-flame pl-6">
                  <blockquote className="font-serif text-xl leading-relaxed text-ink">
                    “{v.quote}”
                  </blockquote>
                  <figcaption className="mt-6">
                    <p className="text-sm font-medium text-ink">{v.name}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-1">{v.role}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Consulting teaser */}
      <section className="rule" data-testid="consulting-teaser-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28 grid gap-12 lg:grid-cols-2 items-center">
          <Reveal>
            <p className="eyebrow">1:1 Artist Consulting</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
              Clarity, strategy, and intentional growth for artists.
            </h2>
            <p className="mt-6 text-neutral-600 leading-relaxed max-w-lg">
              Personalized support for emerging and established artists: artist development,
              exhibition strategy, and curated event assistance, shaped from real rooms and real shows.
            </p>
            <Link
              to="/curatorial#consulting"
              data-testid="consulting-teaser-link"
              className="mt-8 inline-flex items-center gap-2 bg-ink text-white px-7 py-3.5 text-sm tracking-wide hover:bg-royal transition-colors"
            >
              Explore services <ArrowRight size={16} />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid gap-px bg-neutral-200 border border-neutral-200">
              {[
                ["01", "Artist Development", "Creative identity, portfolio direction, artist statements, and positioning."],
                ["02", "Exhibition Strategy", "Concept development, planning, presentation, and audience experience."],
                ["03", "Curated Event Assistance", "From rough concept to fully realized flagship experience."],
              ].map(([n, t, d]) => (
                <div key={n} className="bg-white p-7 flex gap-6">
                  <span className="text-xs tracking-[0.25em] text-azure mt-1">{n}</span>
                  <div>
                    <h3 className="font-serif text-xl text-ink">{t}</h3>
                    <p className="mt-1.5 text-sm text-neutral-600">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="rule" data-testid="home-cta-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-24 md:py-32 text-center">
          <Reveal>
            <p className="eyebrow">Begin a conversation</p>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl tracking-tight text-ink max-w-3xl mx-auto">
              Collect a work, commission a piece, or build a room together.
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="home-cta-contact"
                className="bg-royal text-white px-8 py-4 text-sm tracking-wide hover:bg-royal-deep transition-colors"
              >
                Contact &amp; Collaboration
              </Link>
              <Link
                to="/gallery"
                data-testid="home-cta-gallery"
                className="border border-neutral-300 px-8 py-4 text-sm tracking-wide text-ink hover:border-royal hover:text-royal transition-colors"
              >
                Browse the Artwork
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
